import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Users, ShoppingBag, ArrowRightLeft, Package, FileText,
  Wallet, UserCog, Globe, ArrowRight, Check, ScanSearch, Link2, FileSearch,
  Boxes, ClipboardCheck, ChevronDown,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { api, eur } from "../lib/api";

const FEATURES = [
  { icon: Package, title: "Produkte & Bestand", text: "Produkte und einzelne Artikel mit Seriennummer, IMEI, Zustand und Lagerort zentral verwalten." },
  { icon: ShoppingBag, title: "Ankauf", text: "Ankäufe dokumentieren, Verkäufer mit Identifikationsdaten erfassen und Kaufverträge automatisch erstellen." },
  { icon: ArrowRightLeft, title: "Verkauf", text: "Artikel verkaufen, Rechnungen erstellen und Gewinn automatisch berechnen – inklusive §25a-Differenzbesteuerung." },
  { icon: Boxes, title: "Warenwirtschaft", text: "Bestände, Lagerdauer, Bestandswert und Status jedes einzelnen Artikels jederzeit im Griff." },
  { icon: Users, title: "Kunden", text: "Digitale Kundenakte mit Käufen, Rechnungen, Verträgen und Dokumenten. Einmal erfassen – überall verwenden." },
  { icon: FileText, title: "Dokumente", text: "Verträge, Rechnungen und Ausweise zentral verwalten. Scannen per Handy, PDF-Erstellung, Druck." },
  { icon: ClipboardCheck, title: "Verträge", text: "Professionelle Kaufverträge automatisch aus dem Vorgang – mit digitaler Unterschrift direkt auf dem Smartphone." },
  { icon: Wallet, title: "Finanzen", text: "Einkauf, Verkauf, Umsatz, Gewinn, Marge und Steuerwerte – Monats- und Jahresauswertung auf Knopfdruck." },
  { icon: Sparkles, title: "KI-Assistent", text: "L19 KI erkennt Produkte, erfasst Daten, analysiert Dokumente und unterstützt dein Team bei täglichen Aufgaben." },
];

const DEMO_STEPS = ["Analysieren", "Produkt erkennen", "Daten extrahieren", "Artikel suchen", "Kunde zuordnen", "Import vorbereitet"];

const FLOW = [
  { n: "01", title: "Kunde auswählen", text: "Bestehenden Kunden verwenden oder neuen Kunden direkt im Vorgang anlegen." },
  { n: "02", title: "Artikel importieren", text: "Link, Foto, EAN, Barcode oder Dokument verwenden – ohne Vorwissen über die Produktart." },
  { n: "03", title: "KI analysiert", text: "OpenAI erkennt Produkt, Kategorie und alle relevanten technischen Daten." },
  { n: "04", title: "Prüfen", text: "Automatisch erkannte Daten kontrollieren und bei Bedarf anpassen." },
  { n: "05", title: "Abschließen", text: "Vertrag, Rechnung, Bestand und Dokumentation werden automatisch aktualisiert." },
];

const FAQS = [
  ["Was ist L19?", "L19 ist eine moderne All-in-One-Plattform für Händler: Ankauf, Verkauf, Warenwirtschaft, Kunden, Dokumente, Verträge und Finanzen – alles an einem Ort, unterstützt durch intelligente KI."],
  ["Für welche Händler ist sie geeignet?", "Für alle Ankaufs- und Wiederverkaufsunternehmen: Elektronik, Werkzeug, Fahrräder, Luxuswaren und mehr – überall dort, wo einzelne Artikel mit Seriennummer verfolgt werden müssen."],
  ["Wie funktioniert der KI-Import?", "Sie geben einen Produktlink, eine EAN, ein Foto oder ein Dokument ein. Die KI analysiert die Daten, schlägt Produktdaten vor und sucht Dubletten in Ihrem Bestand. Sie prüfen und bestätigen – erst dann wird gespeichert."],
  ["Kann ich Produkte per Link importieren?", "Ja. Fügen Sie einen Produktlink ein, die KI extrahiert Marke, Modell, technische Daten, EAN und Beschreibung automatisch."],
  ["Kann ich Kunden verwalten?", "Ja, jeder Kunde erhält eine digitale Akte mit allen Käufen, Verkäufen, Rechnungen, Verträgen und Dokumenten."],
  ["Kann ich Verträge drucken?", "Ja. Ankaufverträge werden automatisch erstellt, können direkt auf dem Tablet unterschrieben und als PDF gedruckt oder gespeichert werden."],
  ["Kann ich Mitarbeiter hinzufügen?", "Ja, mit individuellen Rollen: Administrator, Mitarbeiter, Lager, Buchhaltung oder Lesemodus."],
  ["Wie funktioniert die monatliche Abrechnung?", "Sie wählen einen Tarif und hinterlegen eine Zahlungsmethode. Die Abrechnung erfolgt monatlich automatisch über Stripe. Rechnungen finden Sie jederzeit in Ihrem Konto."],
  ["Sind die Daten anderer Unternehmen getrennt?", "Ja. Jedes Unternehmen arbeitet in einem vollständig getrennten Mandanten. Die Trennung wird auf Datenbank- und Backend-Ebene durchgesetzt."],
  ["Kann ich jederzeit kündigen?", "Ja, monatlich kündbar ohne Mindestlaufzeit. Ihre Daten können Sie vorher als CSV exportieren."],
];

function DemoAnimation() {
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);
  const [url, setUrl] = useState("https://example-shop.de/apple-iphone-15-pro-256gb");

  const start = () => {
    setRunning(true);
    setStep(0);
    DEMO_STEPS.forEach((_, i) => setTimeout(() => setStep(i + 1), 700 * (i + 1)));
    setTimeout(() => setRunning(false), 700 * DEMO_STEPS.length + 200);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-6 sm:p-8" data-testid="ki-demo">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-4">Vom Produktlink zum fertigen Artikel</div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 border border-zinc-200 rounded-lg px-3 py-2.5 bg-zinc-50">
          <Link2 className="w-4 h-4 text-zinc-400 shrink-0" />
          <input data-testid="ki-demo-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            className="bg-transparent text-sm w-full focus:outline-none font-mono text-zinc-600" />
        </div>
        <button onClick={start} disabled={running} data-testid="ki-demo-start-button"
          className="bg-zinc-900 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50">
          {running ? "Analyse läuft…" : "KI-Analyse starten"}
        </button>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-6 gap-2">
        {DEMO_STEPS.map((s, i) => (
          <div key={s} className={`text-center text-[11px] font-mono px-2 py-2 rounded-md border transition-all duration-300 ${
            step > i ? "bg-zinc-900 text-white border-zinc-900" : step === i && running ? "border-zinc-900 text-zinc-900 step-active" : "border-zinc-200 text-zinc-400"}`}>
            {s}
          </div>
        ))}
      </div>
      {step >= DEMO_STEPS.length && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mt-6 border border-zinc-200 rounded-xl p-5 bg-zinc-50" data-testid="ki-demo-result">
          <div className="flex flex-wrap justify-between gap-4">
            <div>
              <div className="font-display font-semibold text-lg text-zinc-900">Apple iPhone 15 Pro</div>
              <div className="text-sm text-zinc-500 mt-1">256 GB · Titan Natur · Zustand: Sehr gut</div>
              <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1 text-xs font-mono text-zinc-500">
                <span>EAN: 0195949032712</span><span>Modell: A3101</span>
                <span>EK-Empfehlung: 650,00 €</span><span>Kunde: Max Mustermann</span>
              </div>
            </div>
            <div className="flex items-end">
              <Link to="/registrieren" data-testid="ki-demo-create-article"
                className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-zinc-700 transition-colors flex items-center gap-2">
                Artikel erstellen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function DashboardMock() {
  return (
    <div className="relative" data-testid="hero-dashboard-mock">
      <div className="absolute -inset-4 grid-pattern rounded-3xl" />
      <div className="relative bg-white border border-zinc-200 rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-100">
          <div className="w-2.5 h-2.5 rounded-full bg-zinc-200" /><div className="w-2.5 h-2.5 rounded-full bg-zinc-200" /><div className="w-2.5 h-2.5 rounded-full bg-zinc-200" />
          <div className="ml-3 text-[10px] font-mono text-zinc-400">app.l19.de/dashboard</div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-4 gap-3">
            {[["Umsatz heute", "2.340 €"], ["Gewinn", "486 €"], ["Im Lager", "128"], ["Offene Vorgänge", "3"]].map(([l, v]) => (
              <div key={l} className="border border-zinc-100 rounded-lg p-3 bg-zinc-50/50">
                <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-400">{l}</div>
                <div className="font-display font-semibold text-zinc-900 mt-1 text-sm sm:text-base">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 border border-zinc-100 rounded-lg p-4">
            <div className="flex items-end gap-2 h-28">
              {[40, 65, 45, 80, 55, 90, 70, 100, 62, 85, 74, 95].map((h, i) => (
                <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.3 + i * 0.05, duration: 0.4 }}
                  className={`flex-1 rounded-t ${i % 3 === 2 ? "bg-zinc-300" : "bg-zinc-900"}`} />
              ))}
            </div>
            <div className="mt-2 flex justify-between text-[9px] font-mono text-zinc-400"><span>JAN</span><span>DEZ</span></div>
          </div>
          <div className="mt-4 space-y-2">
            {[["AN-2026-00087", "iPhone 15 Pro · 650,00 €", "Unterschrieben"], ["RE-2026-00125", "MacBook Air · 899,00 €", "Bezahlt"]].map(([n, d, s]) => (
              <div key={n} className="flex items-center justify-between border border-zinc-100 rounded-lg px-3 py-2.5">
                <span className="text-xs font-mono text-zinc-500">{n}</span>
                <span className="text-xs text-zinc-700 hidden sm:block">{d}</span>
                <span className="text-[10px] font-mono bg-zinc-900 text-white px-2 py-0.5 rounded-full">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900" data-testid="landing-page">
      <header className="sticky top-0 z-40 bg-[#FAFAFA]/80 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center"><span className="text-white text-[10px] font-bold font-mono">L19</span></div>
            <span className="font-display font-semibold tracking-tight text-lg">L<span className="text-zinc-400">19</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-600">
            <a href="#features" className="hover:text-zinc-900 transition-colors">Funktionen</a>
            <a href="#ablauf" className="hover:text-zinc-900 transition-colors">Ablauf</a>
            <a href="#preise" className="hover:text-zinc-900 transition-colors">Preise</a>
            <a href="#faq" className="hover:text-zinc-900 transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-link" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">Einloggen</Link>
            <Link to="/registrieren" data-testid="landing-register-cta"
              className="bg-zinc-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-zinc-700 transition-colors">
              Kostenlos testen
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-mono bg-zinc-900 text-zinc-100 rounded-full mb-6">
              <Sparkles className="w-3 h-3" /> L19 · Die intelligente Plattform für Händler
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Dein Händlergeschäft. <span className="text-zinc-400">Ein System.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-lg">
              Verwalte Produkte, Ankauf, Verkauf, Kunden, Dokumente, Verträge und Finanzen zentral – unterstützt durch intelligente KI.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/registrieren" data-testid="hero-cta-test"
                className="bg-zinc-900 text-white font-medium px-6 py-3 rounded-lg hover:bg-zinc-700 transition-colors flex items-center gap-2">
                Kostenlos starten <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#demo" data-testid="hero-cta-demo"
                className="border border-zinc-300 text-zinc-900 font-medium px-6 py-3 rounded-lg hover:border-zinc-900 transition-colors">
                Demo ansehen
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs font-mono text-zinc-400">
              <span>14 Tage kostenlos</span><span>·</span><span>Monatlich kündbar</span><span>·</span><span>DSGVO-konform</span>
            </div>
          </motion.div>
          <DashboardMock />
        </div>
      </section>

      <section id="demo" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <DemoAnimation />
      </section>

      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Funktionen</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Alles, was Ihr Handel braucht. Ein System.</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-zinc-200 hover:border-zinc-400 transition-colors rounded-xl p-6 shadow-sm" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <f.icon className="w-5 h-5 text-zinc-900" />
              <h3 className="font-display font-medium text-lg mt-4 tracking-tight">{f.title}</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="l19-ki" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-zinc-900 text-white rounded-2xl p-8 sm:p-12" data-testid="l19-ki-section">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">L19 KI</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Dein Assistent für das Tagesgeschäft.</h2>
              <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed">
                L19 KI arbeitet im Hintergrund mit: Sie erkennt Produkte, erfasst Daten und analysiert Dokumente –
                dein Team prüft und bestätigt. Die KI erfindet keine Informationen: Was nicht sicher bekannt ist,
                wird klar gekennzeichnet und einem Mitarbeiter vorgelegt.
              </p>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3 text-sm text-zinc-300">
              {["Produkte automatisch erkennen", "Produktdaten automatisch erfassen", "Verkaufstexte erstellen",
                "Preise analysieren", "Margen berechnen", "Kundeninformationen zusammenfassen",
                "Dokumente analysieren", "Arbeitsabläufe automatisieren"].map((f) => (
                <li key={f} className="flex items-start gap-2 border border-zinc-800 rounded-lg px-3 py-2.5">
                  <Sparkles className="w-4 h-4 mt-0.5 text-zinc-500 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="ablauf" className="bg-zinc-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">So funktioniert es</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Vom Ankauf zum Gewinn in 5 Schritten</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {FLOW.map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`flow-step-${s.n}`}>
                <div className="font-mono text-4xl text-zinc-700">{s.n}</div>
                <h3 className="font-display font-medium mt-3">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="preise" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">Preise</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Fair und transparent. Monatlich kündbar.</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {(plans.length ? plans : []).map((p, i) => (
            <motion.div key={p.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className={`rounded-2xl p-8 border ${p.key === "professional" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200"}`}
              data-testid={`plan-card-${p.key}`}>
              <div className="font-mono text-xs uppercase tracking-wider opacity-60">{p.name}</div>
              <div className="mt-4 font-display text-4xl font-bold tracking-tight">
                {p.price_monthly != null ? eur(p.price_monthly) : "Individuell"}
                {p.price_monthly != null && <span className="text-base font-normal opacity-60">/Monat</span>}
              </div>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link to={p.enterprise ? "/registrieren?plan=enterprise" : `/registrieren?plan=${p.key}`} data-testid={`plan-cta-${p.key}`}
                className={`mt-8 block text-center font-medium px-4 py-2.5 rounded-lg transition-colors ${
                  p.key === "professional" ? "bg-white text-zinc-900 hover:bg-zinc-200" : "bg-zinc-900 text-white hover:bg-zinc-700"}`}>
                {p.enterprise ? "Kontakt aufnehmen" : "Jetzt starten"}
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">FAQ</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Häufige Fragen</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left text-sm font-medium">{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-zinc-600 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <footer className="border-t border-zinc-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-zinc-900 rounded-md flex items-center justify-center"><span className="text-white text-[9px] font-bold font-mono">L19</span></div>
            <span className="text-sm font-medium">L19</span>
          </div>
          <div className="flex gap-6 text-xs text-zinc-500">
            <a href="#" className="hover:text-zinc-900">Impressum</a>
            <a href="#" className="hover:text-zinc-900">Datenschutz</a>
            <a href="#" className="hover:text-zinc-900">AGB</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
